import { format } from "date-fns-tz"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import BasePopover from "src/components/BasePopover"
import BaseSlider from "src/components/BaseSlider"
import Form from "src/components/Form"
import SearchableSelect from "src/components/SearchableSelect"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"
import Tooltip from "src/components/Tooltip"

import useDebounce from "src/hooks/use_debounce"
import useWindowSize from "src/hooks/use_window_size"

import { snakecaseToTitlecase } from "src/utils/string_helpers"
import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

const MIN_NIGHTS = 0
const MAX_NIGHTS = 365

const PAYMENT_STATUS_OPTIONS = [
  { id: "awaiting_refund", name: "Awaiting Refund" },
  { id: "payment_declined", name: "Payment Declined" },
  { id: "missing_card", name: "Missing Card" },
  { id: "overdue_balance", name: "Overdue Balance" },
]

const Filters = ({ table, reservationStatuses, spaceGroups, exportPath }) => {
  const { isXLScreen } = useWindowSize()
  const marinaSlug = getCurrentMarinaSlug()

  const allAssignmentOptions = [
    { id: "all_unassigned", name: "Unassigned" },
    { id: "all_assigned", name: "Assigned" },
    ...spaceGroups.map((sg) => ({ id: sg.id.toString(), name: sg.name })),
    { id: "groupless", name: "Groupless Spaces" },
  ]

  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.searchTerm || ""
  )
  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("searchTerm", value)
  })

  useEffect(() => {
    if (!table.filters.searchTerm) setSearchInputValue("")
  }, [table.filters.searchTerm])

  // Local Date objects for the date pickers (table stores formatted strings)
  const [inMarinaStartDate, setInMarinaStartDate] = useState(null)
  const [inMarinaEndDate, setInMarinaEndDate] = useState(null)

  // Sync local date state when FilterChips clears the dates
  useEffect(() => {
    if (!table.filters.inMarinaStartDate) setInMarinaStartDate(null)
  }, [table.filters.inMarinaStartDate])

  useEffect(() => {
    if (!table.filters.inMarinaEndDate) setInMarinaEndDate(null)
  }, [table.filters.inMarinaEndDate])

  // Local slider position; synced back to null when the nights filter is cleared
  const [nightsRange, setNightsRange] = useState([MIN_NIGHTS, MAX_NIGHTS])

  useEffect(() => {
    if (table.filters.nightsMin == null && table.filters.nightsMax == null) {
      setNightsRange([MIN_NIGHTS, MAX_NIGHTS])
    }
  }, [table.filters.nightsMin, table.filters.nightsMax])

  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchInputValue(value)
    debouncedSetSearch(value.trim())
  }

  const handleSpaceGroupChange = (newValues) => {
    const current = table.filters.spaceGroups || []
    const added = newValues.find((v) => !current.includes(v))

    if (added === "all_assigned" || added === "all_unassigned") {
      table.setFilter("spaceGroups", [added])
    } else {
      table.setFilter(
        "spaceGroups",
        newValues.filter((v) => v !== "all_assigned" && v !== "all_unassigned")
      )
    }
  }

  const handleInMarinaDateChange = (field, date) => {
    const formatted = date ? format(date, "yyyy-MM-dd") : null

    if (field === "inMarinaStartDate") {
      setInMarinaStartDate(date)
      if (!date) {
        setInMarinaEndDate(null)
        table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
      } else {
        table.setFilter("inMarinaStartDate", formatted)
      }
    } else {
      setInMarinaEndDate(date)
      if (!date) {
        setInMarinaStartDate(null)
        table.setFilters({ inMarinaStartDate: null, inMarinaEndDate: null })
      } else {
        table.setFilter("inMarinaEndDate", formatted)
      }
    }
  }

  const handleNightsRangeChange = (values) => {
    setNightsRange(values)
    table.setFilters({
      nightsMin: values[0] === MIN_NIGHTS ? null : values[0],
      nightsMax: values[1] === MAX_NIGHTS ? null : values[1],
    })
  }

  const handleNightsMinInput = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null
    table.setFilter("nightsMin", value)
  }

  const handleNightsMaxInput = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null
    table.setFilter("nightsMax", value)
  }

  const renderSearch = () => (
    <Form.IconTextField
      icon={<i className="icon icon-search-mdi text-xl text-gray-400" />}
      id="reservation-search"
      isClearable
      onChange={handleSearchChange}
      onClearSelection={() => {
        setSearchInputValue("")
        table.setFilter("searchTerm", "")
      }}
      position="left"
      value={searchInputValue}
      placeholder="Search by name, boat, email, or ID"
    />
  )

  const renderStatusFilter = () => (
    <Select
      multiple
      value={table.filters.statuses}
      onValueChange={(values) => table.setFilter("statuses", values)}
    >
      <SelectTrigger variant="button" className="w-full">
        Status
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {reservationStatuses.map((status) => (
          <SelectItem key={status} value={status}>
            {snakecaseToTitlecase(status)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderInMarinaFilter = () => (
    <BasePopover label="In Marina">
      <div className="space-y-2 p-4">
        <div className="flex flex-col">
          <Form.Label htmlFor="inMarinaStartDate">Start Date</Form.Label>
          <Form.DatePicker
            value={inMarinaStartDate}
            onChange={(date) =>
              handleInMarinaDateChange("inMarinaStartDate", date)
            }
            id="startDate"
            maxDate={inMarinaEndDate}
            todayButton="Today"
            portalId={null}
          />
        </div>
        <div className="flex flex-col">
          <Form.Label htmlFor="inMarinaEndDate">End Date</Form.Label>
          <Form.DatePicker
            value={inMarinaEndDate}
            onChange={(date) =>
              handleInMarinaDateChange("inMarinaEndDate", date)
            }
            id="endDate"
            minDate={inMarinaStartDate}
            todayButton="Today"
            portalId={null}
          />
        </div>
      </div>
    </BasePopover>
  )

  const renderSpaceGroupsFilter = () => (
    <SearchableSelect
      multiple
      value={table.filters.spaceGroups}
      onValueChange={handleSpaceGroupChange}
    >
      <SearchableSelect.Trigger variant="button" className="w-full">
        Assignment
      </SearchableSelect.Trigger>
      <SearchableSelect.FilteredList
        options={allAssignmentOptions}
        placeholder="Search groups..."
      />
    </SearchableSelect>
  )

  const renderPaymentStatusFilter = () => (
    <Select
      multiple
      value={table.filters.paymentStatuses}
      onValueChange={(values) => table.setFilter("paymentStatuses", values)}
    >
      <SelectTrigger variant="button" className="w-full">
        Payment
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {PAYMENT_STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderNightsFilter = () => (
    <BasePopover label="Nights" width={isXLScreen ? "400px" : "300px"}>
      <div className="grid grid-cols-12 gap-2 space-y-2 px-6 pb-6 pt-8">
        <div className="col-span-12 flex justify-between">
          <span>{MIN_NIGHTS} nights</span>
          <span>
            {MAX_NIGHTS} {pluralize("night", MAX_NIGHTS)}
          </span>
        </div>
        <div className="col-span-12">
          <BaseSlider
            value={nightsRange}
            onValueChange={handleNightsRangeChange}
            min={MIN_NIGHTS}
            max={MAX_NIGHTS}
          />
        </div>
        <div className="col-span-6 flex flex-col">
          <Form.Label htmlFor="nightsMin">Minimum</Form.Label>
          <Form.IconTextField
            onChange={handleNightsMinInput}
            icon="nights"
            position="right"
            id="nightsMin"
            value={table.filters.nightsMin ?? ""}
            type="number"
          />
        </div>
        <div className="col-span-6 flex flex-col">
          <div className="flex">
            <Form.Label htmlFor="nightsMax">Maximum</Form.Label>
            <div className="ml-2">
              <Tooltip
                text="Dynamically changes based on longest reservation"
                placement="top"
                variant="dark"
              >
                <i className="icon icon-info" />
              </Tooltip>
            </div>
          </div>
          <Form.IconTextField
            onChange={handleNightsMaxInput}
            id="nightsMax"
            icon="nights"
            position="right"
            value={table.filters.nightsMax ?? ""}
            type="number"
          />
        </div>
      </div>
    </BasePopover>
  )

  return (
    <div className="mt-2 flex flex-col px-4 pb-6 lg:p-0 xl:flex-row">
      <div className="order-2 flex w-full flex-col xl:order-1 xl:flex-row">
        <div className="w-full pb-2 xl:mr-2 xl:w-[336px] xl:p-0">
          {renderSearch()}
        </div>
        <div className="flex w-full xl:w-min">
          <div className="flex w-full flex-wrap space-y-2 sm:flex-nowrap sm:space-x-2 sm:space-y-0">
            {renderStatusFilter()}
            {renderInMarinaFilter()}
            {renderSpaceGroupsFilter()}
            {renderNightsFilter()}
            {renderPaymentStatusFilter()}
          </div>
        </div>
      </div>
      <div className="order-1 mb-2 flex w-full xl:order-2 xl:m-0 xl:w-min">
        <a
          href={`/manage/${marinaSlug}/reservations/new`}
          className="btn btn-primary w-full"
        >
          Add new
        </a>
      </div>
    </div>
  )
}

Filters.propTypes = {
  table: PropTypes.object.isRequired,
  reservationStatuses: PropTypes.array.isRequired,
  spaceGroups: PropTypes.array.isRequired,
  exportPath: PropTypes.string,
}

export default Filters
